"use client";

import { useEffect, useRef, useState } from "react";
import type { Map as MapLibreMap, Marker as MapLibreMarker } from "maplibre-gl";
import type { Place } from "../content/site-data";

type Filter = "all" | "building" | "neighbor";

const PLACE_COORDINATES: Record<string, [number, number]> = {
  "main-lodge": [-88.5677162, 48.1663087],
  "bath-house": [-88.5678212, 48.1662698],
  "boys-cabin": [-88.5678444, 48.1662078],
  "sleeping-cabin": [-88.5677373, 48.1661051],
  "tool-shed": [-88.5677099, 48.1664242],
  "boat-house": [-88.5673535, 48.1664121],
  "glass-house": [-88.5674384, 48.166497],
  "crystal-cove": [-88.5899262, 48.1585558],
  "johnson-island": [-88.5854981, 48.1473235],
  "belle-isle": [-88.5876331, 48.1511954],
};

export default function ExploreIslandMap({ places, filter, selected, onSelect }: { places: Place[]; filter: Filter; selected: Place; onSelect: (place: Place) => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const markersRef = useRef<MapLibreMarker[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    let cancelled = false;

    import("maplibre-gl").then((maplibregl) => {
      if (cancelled || !containerRef.current) return;
      const map = new maplibregl.Map({
        container: containerRef.current,
        style: {
          version: 8,
          sources: {
            openStreetMap: {
              type: "raster",
              tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
              tileSize: 256,
              attribution: "© OpenStreetMap contributors",
              maxzoom: 19,
            },
          },
          layers: [
            { id: "paper", type: "background", paint: { "background-color": "#d9e2dc" } },
            { id: "open-street-map", type: "raster", source: "openStreetMap", paint: { "raster-saturation": -0.48, "raster-contrast": -0.14, "raster-brightness-min": 0.18, "raster-brightness-max": 0.96 } },
          ],
        },
        center: [-88.56761, 48.1663],
        zoom: 18.2,
        attributionControl: false,
        cooperativeGestures: true,
      });
      map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");
      map.addControl(new maplibregl.NavigationControl({ showCompass: true, showZoom: true }), "top-right");
      mapRef.current = map;
      setReady(true);
    });

    return () => {
      cancelled = true;
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!ready || !mapRef.current) return;
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    import("maplibre-gl").then((maplibregl) => {
      if (!mapRef.current) return;
      places.filter((place) => filter === "all" || place.type === filter).forEach((place) => {
        const marker = document.createElement("button");
        marker.type = "button";
        marker.className = `explore-map-marker ${place.type}${selected.id === place.id ? " selected" : ""}`;
        marker.setAttribute("aria-label", `View ${place.name}`);
        marker.innerHTML = `<i></i><span>${place.name}</span>`;
        marker.addEventListener("click", () => onSelect(place));
        markersRef.current.push(new maplibregl.Marker({ element: marker, anchor: "left", offset: [-5, 0] }).setLngLat(PLACE_COORDINATES[place.id]).addTo(mapRef.current!));
      });
    });

    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
    };
  }, [filter, onSelect, places, ready, selected.id]);

  useEffect(() => {
    if (!ready || !mapRef.current) return;
    if (filter === "all") {
      mapRef.current.fitBounds([[-88.5912, 48.1464], [-88.5661, 48.1674]], { padding: 80, duration: 900 });
    } else if (filter === "neighbor") {
      mapRef.current.fitBounds([[-88.5912, 48.1464], [-88.5842, 48.1595]], { padding: 70, duration: 900 });
    } else {
      mapRef.current.easeTo({ center: [-88.56761, 48.1663], zoom: 18.2, duration: 900 });
    }
  }, [filter, ready]);

  return (
    <div className="explore-live-map">
      <div ref={containerRef} className="explore-map-canvas" aria-label="Interactive map of Capt. Kidd Island and its buildings" />
      <div className="map-paper-wash" aria-hidden="true" />
      <div className="explore-map-note"><b>Buildings of Capt. Kidd</b><span>Family-marked locations · Select a marker</span></div>
    </div>
  );
}
